import React, { useState } from 'react';
import { Receipt } from 'lucide-react';
import { MenuItem, TextField } from '@mui/material';
import { DataGrid, type GridColDef, type GridPaginationModel } from '@mui/x-data-grid';
import { PaymentDetailDialog } from '../components/PaymentDetailDialog';
import { PaymentStatusChip } from '../components/PaymentStatusChip';
import { usePaymentList } from '../hooks/usePayments';
import { formatDateTime, formatVnd } from '../format';
import { PAYMENT_STATUSES, type PaymentRow, type PaymentStatus } from '../types';

const startOfDay = (value: string) =>
  value ? new Date(`${value}T00:00:00`).toISOString() : undefined;
const endOfDay = (value: string) =>
  value ? new Date(`${value}T23:59:59.999`).toISOString() : undefined;

export const PaymentListPage: React.FC = () => {
  const [status, setStatus] = useState<PaymentStatus | ''>('');
  const [kind, setKind] = useState('');
  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 20 });
  const resetPage = () => setPagination((p) => ({ ...p, page: 0 }));

  const { data, isLoading } = usePaymentList({
    page: pagination.page + 1,
    limit: pagination.pageSize,
    status,
    kind,
    q: search.trim() || undefined,
    from: startOfDay(from),
    to: endOfDay(to),
  });

  const columns: GridColDef<PaymentRow>[] = [
    {
      field: 'createdAt',
      headerName: 'Created',
      width: 180,
      valueGetter: (_v, row) => formatDateTime(row.createdAt),
    },
    {
      field: 'user',
      headerName: 'Customer',
      flex: 1,
      minWidth: 220,
      valueGetter: (_v, row) => row.user.email,
    },
    {
      field: 'item',
      headerName: 'Item',
      width: 170,
      valueGetter: (_v, row) => row.item?.name ?? row.kind,
    },
    {
      field: 'invoiceNumber',
      headerName: 'Invoice',
      width: 190,
      valueGetter: (_v, row) => row.invoiceNumber ?? '',
    },
    {
      field: 'listAmountMinor',
      headerName: 'List',
      width: 120,
      valueGetter: (_v, row) => formatVnd(row.listAmountMinor),
    },
    {
      field: 'discountMinor',
      headerName: 'Discount',
      width: 150,
      valueGetter: (_v, row) =>
        row.discountMinor > 0
          ? `-${formatVnd(row.discountMinor)}${row.coupon ? ` · ${row.coupon.code}` : ''}`
          : '',
    },
    {
      field: 'amountMinor',
      headerName: 'Amount',
      width: 130,
      valueGetter: (_v, row) => formatVnd(row.amountMinor),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 150,
      renderCell: (p) => <PaymentStatusChip status={p.row.status} />,
    },
    {
      field: 'paidAt',
      headerName: 'Paid',
      width: 180,
      valueGetter: (_v, row) => formatDateTime(row.paidAt),
    },
  ];

  return (
    <div className="space-y-4 p-6">
      <div className="flex items-center gap-2">
        <Receipt size={22} />
        <h1 className="text-xl font-semibold">Payments</h1>
      </div>
      <div className="flex flex-wrap gap-3">
        <TextField
          size="small"
          label="Search email or invoice"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            resetPage();
          }}
          sx={{ minWidth: 240 }}
        />
        <TextField
          select
          size="small"
          label="Status"
          value={status}
          sx={{ minWidth: 160 }}
          onChange={(e) => {
            setStatus(e.target.value as PaymentStatus | '');
            resetPage();
          }}
        >
          <MenuItem value="">All</MenuItem>
          {PAYMENT_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {s.replace('_', ' ')}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Type"
          value={kind}
          sx={{ minWidth: 150 }}
          onChange={(e) => {
            setKind(e.target.value);
            resetPage();
          }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="TOKEN_PACK">Token pack</MenuItem>
          <MenuItem value="PACKAGE">Package</MenuItem>
        </TextField>
        <TextField
          size="small"
          type="date"
          label="From"
          InputLabelProps={{ shrink: true }}
          value={from}
          onChange={(e) => {
            setFrom(e.target.value);
            resetPage();
          }}
        />
        <TextField
          size="small"
          type="date"
          label="To"
          InputLabelProps={{ shrink: true }}
          value={to}
          onChange={(e) => {
            setTo(e.target.value);
            resetPage();
          }}
        />
      </div>
      <DataGrid
        autoHeight
        rows={data?.items ?? []}
        getRowId={(row) => row.publicId}
        columns={columns}
        loading={isLoading}
        rowCount={data?.total ?? 0}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        pageSizeOptions={[20, 50, 100]}
        disableRowSelectionOnClick
        disableColumnSorting
        onRowClick={(params) => setSelected(params.row.publicId)}
        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
        localeText={{ noRowsLabel: 'No payments match these filters.' }}
      />
      <PaymentDetailDialog publicId={selected} onClose={() => setSelected(null)} />
    </div>
  );
};
